import { useAnalytics } from "@codat/analytics";
import {
  BinIcon,
  Button,
  DataTable,
  IconButton,
  Pagination,
  Typography,
} from "@codat/orchard-ui";
import type {
  ColumnDefinition,
  RowInfo,
} from "@codat/orchard-ui/dist/es/components/DataTable/types";
import { CodatClientInvitation } from "api/clientInvites";
import { usePermissionsContext } from "contexts/PermissionsContext";
import { format } from "date-fns";
import React, { useCallback, useMemo } from "react";

import styles from "./InvitesTable.module.css";

type EmailCellModel = {
  key: "email";
  value: string;
};
type RoleCellModel = {
  key: "role";
  value: string;
};
type StatusCellModel = {
  key: "status";
  value: string;
};
type CtaCellModel = {
  key: "cta";
  value: {
    email: string;
    onResendButtonClick?: () => void;
    onDeleteButtonClick?: () => void;
    accepted: boolean;
  };
};

type TableRow = [EmailCellModel, RoleCellModel, StatusCellModel, CtaCellModel];

const EmailCell: React.FC<{ value: string }> = ({ value }) => (
  <Typography className={styles.textCell} data-testid="invites-email" as="p">
    {value}
  </Typography>
);

const RoleCell: React.FC<{ value: string }> = ({ value }) => (
  <Typography className={styles.textCell} data-testid="invites-role" as="p">
    {value}
  </Typography>
);

const StatusCell: React.FC<{ value: string }> = ({ value }) => (
  <Typography className={styles.textCell} data-testid="invites-status" as="p">
    {value}
  </Typography>
);

const CtaCell: React.FC<Pick<CtaCellModel, "value">> = ({ value }) => {
  const { sendEvent } = useAnalytics();
  return (
    <div className={styles.ctaContainer}>
      {value.onResendButtonClick && !value.accepted && (
        <Button
          className={styles.resendButton}
          size="small"
          label="Resend"
          variant="secondary"
          key={value.email}
          onClick={() => {
            value.onResendButtonClick && value.onResendButtonClick();
            sendEvent("Resend Button - Click");
          }}
          data-testid="invites-table-resend"
        />
      )}
      {value.onDeleteButtonClick && (
        <IconButton
          label="Delete"
          className={styles.deleteButton}
          onClick={() => {
            value.onDeleteButtonClick && value.onDeleteButtonClick();
            sendEvent("Delete Button - Click");
          }}
          data-testid="invites-table-delete"
        >
          <BinIcon />
        </IconButton>
      )}
    </div>
  );
};

const columnDefs: ColumnDefinition<string>[] = [
  {
    key: "email",
    label: "Email",
    component: EmailCell,
  },
  {
    key: "role",
    label: "Role",
    component: RoleCell,
  },
  {
    key: "status",
    label: "Status",
    component: StatusCell,
  },
  {
    key: "cta",
    label: "",
    component: CtaCell,
    align: "right",
  },
];

interface InvitesTableProps {
  invitations: CodatClientInvitation[];
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  totalResults: number;
  isSkeleton: boolean;
  onResendInvite: (invitation: CodatClientInvitation) => void;
  onDeleteInvite: (invitation: CodatClientInvitation) => void;
  disableInteractions?: boolean;
}

export const InvitesTable: React.FC<InvitesTableProps> = ({
  invitations,
  page,
  setPage,
  pageSize,
  setPageSize,
  totalResults,
  isSkeleton,
  onResendInvite,
  onDeleteInvite,
  disableInteractions = false,
}) => {
  const { data: permissions } = usePermissionsContext();
  const { sendEvent } = useAnalytics();

  const isExpired = useCallback(
    (invitation: CodatClientInvitation) =>
      invitation.inviteExpiresOn &&
      new Date(invitation.inviteExpiresOn) < new Date(),
    []
  );

  const getStatus = useCallback(
    (invitation: CodatClientInvitation) => {
      if (invitation.inviteAcceptedOn) {
        return `Invite accepted on 
            ${format(new Date(invitation.inviteAcceptedOn), "MMM d, yyyy")}`;
      }

      if (invitation.inviteDeclinedOn) {
        return `Invite declined on 
            ${format(new Date(invitation.inviteDeclinedOn), "MMM d, yyyy")}`;
      }

      let expiry = "";
      if (invitation.inviteExpiresOn) {
        expiry = `Invite expire${isExpired(invitation) ? "d" : "s"} on 
            ${format(new Date(invitation.inviteExpiresOn), "MMM d, yyyy")}`;
      }

      if (isExpired(invitation)) {
        return "Expired: " + expiry;
      }

      return "Pending: " + expiry;
    },
    [isExpired]
  );

  const tableData = useMemo<RowInfo<TableRow>[]>(
    () =>
      invitations.map((invitation) => {
        const ctaValue: CtaCellModel["value"] = {
          email: invitation.email,
          accepted: invitation.inviteAcceptedOn !== undefined,
        };

        if (permissions?.includes("Edit") && !disableInteractions) {
          ctaValue.onResendButtonClick = () => onResendInvite(invitation);
        }

        if (permissions?.includes("Delete") && !disableInteractions) {
          ctaValue.onDeleteButtonClick = () => onDeleteInvite(invitation);
        }

        return {
          data: [
            {
              key: "email",
              value: invitation.email,
            },
            {
              key: "role",
              value: invitation.clientRole,
            },
            {
              key: "status",
              value: getStatus(invitation),
            },
            {
              key: "cta",
              value: ctaValue,
            },
          ],
        };
      }),
    [
      invitations,
      getStatus,
      onResendInvite,
      permissions,
      onDeleteInvite,
      disableInteractions,
    ]
  );

  return (
    <>
      <DataTable
        columnDefs={columnDefs}
        isSkeleton={isSkeleton}
        rowsInfo={tableData}
        skeletonRows={10}
        className={styles.usersTable}
        data-testid="invites-table"
        isLayoutAuto
        hasBorder
      />
      <Pagination
        label="Number of invites to show per page"
        onUpdatePageIndex={(page) => {
          setPage(page);
          sendEvent("Page Index Change");
        }}
        onUpdatePerPage={(size) => {
          setPageSize(size);
          sendEvent("Page Size Change", { size });
        }}
        pageIndex={page}
        perPage={pageSize}
        perPageOptions={[10, 20, 50]}
        total={totalResults}
      />
    </>
  );
};
